import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectCurrentStep, setCurrentStep } from 'app/sharedSlice';
import TemplateStep from 'features/data-app-builder/steps/TemplateStep';
import DataAppDefinitionStep from 'features/data-app-builder/steps/DataAppDefinitionStep';
import DeploymentConfigurationStep from 'features/data-app-builder/steps/DeploymentConfigurationStep';
import SnowflakeAccountsStep from 'features/native-app-builder/steps/SnowflakeAccountsStep';
import PythonUdfConfigurationStep from 'features/native-app-builder/steps/PythonUdfConfigurationStep';
import SqlUdfConfigurationStep from 'features/native-app-builder/steps/SqlUdfConfigurationStep';
import StreamlitConfigurationStep from 'features/native-app-builder/steps/StreamlitConfigurationStep';
import SpcsConfigurationStep from 'features/data-app-builder/steps/SpcsConfigurationStep';
import SnowflakeAiConfigurationStep from 'features/native-app-builder/steps/SnowflakeAiConfigurationStep';
import CortexAiConfigurationStep from 'features/native-app-builder/steps/CortexAiConfigurationStep';
import CortexAiSearchConfigurationStep from 'features/native-app-builder/steps/CortexAiSearchConfigurationStep';
import SharedDataDatabaseStep from 'features/native-app-builder/steps/SharedDataDatabaseStep';
import SharedDataSchemaStep from 'features/native-app-builder/steps/SharedDataSchemaStep';
import NotebookConfigurationStep from 'features/native-app-builder/steps/NotebookConfigurationStep';
import DataAppSummaryStep from 'features/data-app-builder/steps/DataAppSummaryStep';
import { sendMetrics, MetricType } from 'utils/metrics';
import useDataAppCreatorSteps from 'features/data-app-builder/steps/dataAppBuilderSteps';
import { IStep } from 'utils/types';

export default function DataAppBuilderView(): JSX.Element {
  const steps: IStep[] = useDataAppCreatorSteps();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);

  const setStepAndSendMetric = (step: number): void => {
    sendMetrics(MetricType.create_analytics, step.toString());
    dispatch(setCurrentStep(step));
  };

  const getComponent = (name: string, index: number): React.ReactNode => {
    switch (name) {
      case 'Template':
        return (
          <TemplateStep
            onBack={index === 0 ? () => navigate('/') : () => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Select components':
        return (
          <DataAppDefinitionStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Deployment configuration':
        return (
          <DeploymentConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Summary':
        return (
          <DataAppSummaryStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake accounts':
        return (
          <SnowflakeAccountsStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Python UDF configuration':
        return (
          <PythonUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SQL UDF configuration':
        return (
          <SqlUdfConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Streamlit configuration':
        return (
          <StreamlitConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'SPCS configuration':
        return (
          <SpcsConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Snowflake AI configuration':
        return (
          <SnowflakeAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI configuration':
        return (
          <CortexAiConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Cortex AI Search configuration':
        return (
          <CortexAiSearchConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Database':
        return (
          <SharedDataDatabaseStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Shared Data - Schema':
        return (
          <SharedDataSchemaStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
      case 'Notebook configuration':
        return (
          <NotebookConfigurationStep
            onBack={() => setStepAndSendMetric(index - 1)}
            onContinue={() => setStepAndSendMetric(index + 1)}
          />
        );
    }
  };

  return (
    <>
      {steps.map((step, index) => (
        <Fragment key={`${step.name}-${index}`}>{currentStep === index && getComponent(step.name, index)}</Fragment>
      ))}
    </>
  );
}
